import { useCallback, useEffect, useReducer } from "react";
import type { Report } from "@brutally/shared";
import { HeroGame } from "./screens/HeroGame.js";
import { AnalyzeGame } from "./screens/AnalyzeGame.js";
import { LoadingGame } from "./screens/LoadingGame.js";
import { ReportGame } from "./screens/ReportGame.js";
import { ErrorGame } from "./screens/ErrorGame.js";
import { DashboardGame } from "./screens/DashboardGame.js";
import { LoginGame } from "./screens/LoginGame.js";
import { GAME_LOADING } from "./data/game.js";
import { SAMPLE_REPORT } from "./data/sample.js";
import { analyze } from "./api/analyze.js";
import { fetchMe, signOut } from "./api/auth.js";
import { store } from "./store/sites.js";
import { prettyHost } from "./lib/format.js";

type Screen = "hero" | "analyze" | "loading" | "report" | "dashboard" | "error" | "login";

interface State {
  screen: Screen;
  url: string;
  pitch: string;
  model: string;
  stage: string;
  report: Report | null;
  errorMessage: string;
  toast: string;
  loadingReady: boolean;
  authEmail: string | null;
  authChecked: boolean;
}

type Action =
  | { type: "GO"; screen: Screen }
  | { type: "SET_URL"; v: string }
  | { type: "SET_PITCH"; v: string }
  | { type: "SET_MODEL"; v: string }
  | { type: "SET_STAGE"; v: string }
  | { type: "BEGIN_RUN" }
  | { type: "LOADED"; report: Report }
  | { type: "FAIL"; message: string }
  | { type: "TOAST"; message: string }
  | { type: "CLEAR_TOAST" }
  | { type: "RESET_INPUT" }
  | { type: "AUTH_SET"; email: string | null };

const INITIAL: State = {
  screen: "hero",
  url: "",
  pitch: "",
  model: "",
  stage: "",
  report: null,
  errorMessage: "",
  toast: "",
  loadingReady: false,
  authEmail: null,
  authChecked: false
};

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case "GO": {
      window.scrollTo(0, 0);
      return { ...s, screen: a.screen };
    }
    case "SET_URL": return { ...s, url: a.v };
    case "SET_PITCH": return { ...s, pitch: a.v };
    case "SET_MODEL": return { ...s, model: a.v };
    case "SET_STAGE": return { ...s, stage: a.v };
    case "BEGIN_RUN": return { ...s, screen: "loading", loadingReady: false, report: null, errorMessage: "" };
    case "LOADED": return { ...s, report: a.report, loadingReady: true };
    case "FAIL": return { ...s, errorMessage: a.message, loadingReady: true };
    case "TOAST": return { ...s, toast: a.message };
    case "CLEAR_TOAST": return { ...s, toast: "" };
    case "RESET_INPUT": return { ...s, url: "", pitch: "", model: "", stage: "", report: null, errorMessage: "" };
    case "AUTH_SET": return { ...s, authEmail: a.email, authChecked: true };
    default: return s;
  }
}

const CTA = "Show me the damage";
const MODEL_MAP: Record<string, "B2B" | "B2C" | "B2B2C" | undefined> = {
  B2B: "B2B", B2C: "B2C", B2B2C: "B2B2C"
};
const STAGE_MAP: Record<string, "pre-launch" | "just-launched" | "growing" | undefined> = {
  "Pre-launch": "pre-launch",
  "Just launched": "just-launched",
  Growing: "growing"
};

export default function App() {
  const [s, dispatch] = useReducer(reducer, INITIAL);

  // Auto-clear toast after 2.4s
  useEffect(() => {
    if (!s.toast) return;
    const t = setTimeout(() => dispatch({ type: "CLEAR_TOAST" }), 2400);
    return () => clearTimeout(t);
  }, [s.toast]);

  // Activate Pro via ?dev=1 query param
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("dev") === "1") {
      console.info("[brutally] Dev mode active. Triple-click the wordmark to toggle Pro.");
    }
  }, []);

  // Check sign-in status on load. If signed in, force the local plan to Pro
  // so the dashboard / UI reflects the server-side tier.
  useEffect(() => {
    let cancelled = false;
    fetchMe().then((me) => {
      if (cancelled) return;
      if (me.ok && me.email) {
        dispatch({ type: "AUTH_SET", email: me.email });
        if (store.getState().plan !== "pro") store.setPlan("pro");
      } else {
        dispatch({ type: "AUTH_SET", email: null });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // If we just came back from a sign-in redirect, clean the URL.
  // signed_in=1 → success (toast handled by /api/me success path)
  // auth_error=... → failed magic-link redirect; surface the reason to the user
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let mutated = false;
    if (params.get("signed_in") === "1") {
      params.delete("signed_in");
      mutated = true;
    }
    const err = params.get("auth_error");
    if (err) {
      dispatch({ type: "TOAST", message: `SIGN-IN FAILED: ${err.toUpperCase()}` });
      params.delete("auth_error");
      mutated = true;
    }
    if (mutated) {
      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? "?" + qs : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const showToast = useCallback((message: string) => dispatch({ type: "TOAST", message }), []);

  const startRun = useCallback(
    async (pitchOverride?: string) => {
      dispatch({ type: "BEGIN_RUN" });
      const minTimer = new Promise<void>((r) => setTimeout(r, 5400));
      const apiCall = analyze({
        url: s.url.trim(),
        pitch: (pitchOverride ?? s.pitch).trim() || undefined,
        model: MODEL_MAP[s.model],
        stage: STAGE_MAP[s.stage],
        tier: store.getState().plan
      });
      const [, res] = await Promise.all([minTimer, apiCall]);
      if (!res.ok) {
        dispatch({ type: "FAIL", message: res.message });
      } else {
        dispatch({ type: "LOADED", report: res.report });
      }
    },
    [s.url, s.pitch, s.model, s.stage]
  );

  const onLoadingDone = useCallback(() => {
    if (s.errorMessage) {
      dispatch({ type: "GO", screen: "error" });
    } else if (s.report) {
      dispatch({ type: "GO", screen: "report" });
    } else {
      dispatch({ type: "GO", screen: "error" });
    }
  }, [s.report, s.errorMessage]);

  const fromHero = useCallback(() => {
    dispatch({ type: "GO", screen: "analyze" });
  }, []);

  const onShare = useCallback(() => {
    if (navigator.clipboard && s.report) {
      navigator.clipboard.writeText(`${window.location.origin}?u=${encodeURIComponent(s.report.url)}`);
    }
    store.markShare();
    showToast("SCORE COPIED. GO RUIN THEIR DAY.");
  }, [s.report, showToast]);

  const onSave = useCallback(() => {
    if (!s.report) return;
    const result = store.saveSite(s.report);
    if (result.ok) {
      showToast(`SAVED. ${prettyHost(s.report.url).toUpperCase()} ADDED TO YOUR CABINET.`);
      setTimeout(() => dispatch({ type: "GO", screen: "dashboard" }), 700);
    } else {
      showToast("FREE TRACKS ONE SITE. UPGRADE TO PRO FOR MORE.");
      setTimeout(() => dispatch({ type: "GO", screen: "dashboard" }), 900);
    }
  }, [s.report, showToast]);

  const onRerun = useCallback(() => {
    dispatch({ type: "RESET_INPUT" });
    dispatch({ type: "GO", screen: "hero" });
  }, []);

  const onUpgrade = useCallback(() => {
    store.setPlan("pro");
    showToast("WELCOME TO PRO. THE WHOLE ARCADE IS YOURS.");
  }, [showToast]);

  const onSignIn = useCallback(() => {
    dispatch({ type: "GO", screen: "login" });
  }, []);

  const onSignOut = useCallback(async () => {
    await signOut();
    dispatch({ type: "AUTH_SET", email: null });
    store.setPlan("free");
    showToast("SIGNED OUT.");
  }, [showToast]);

  const onPlayFromDash = useCallback(
    (url?: string) => {
      if (typeof url === "string" && url) {
        dispatch({ type: "SET_URL", v: url });
        dispatch({ type: "GO", screen: "analyze" });
      } else {
        dispatch({ type: "RESET_INPUT" });
        dispatch({ type: "GO", screen: "hero" });
      }
    },
    []
  );

  const onPasteFallback = useCallback(
    (text: string) => {
      dispatch({ type: "SET_PITCH", v: text });
      // Re-run with the pasted text in place of a fetched site
      startRun(text);
    },
    [startRun]
  );

  // Hero sample report uses the SAMPLE_REPORT data (visible only at hero card)
  void SAMPLE_REPORT;

  return (
    <>
      {s.screen === "hero" && (
        <HeroGame
          url={s.url}
          setUrl={(v) => dispatch({ type: "SET_URL", v })}
          onSubmit={fromHero}
          cta={CTA}
          onDashboard={() => dispatch({ type: "GO", screen: "dashboard" })}
          authEmail={s.authEmail}
          authChecked={s.authChecked}
          onSignIn={onSignIn}
          onSignOut={onSignOut}
        />
      )}
      {s.screen === "analyze" && (
        <AnalyzeGame
          url={s.url}
          setUrl={(v) => dispatch({ type: "SET_URL", v })}
          pitch={s.pitch}
          setPitch={(v) => dispatch({ type: "SET_PITCH", v })}
          model={s.model}
          setModel={(v) => dispatch({ type: "SET_MODEL", v })}
          stage={s.stage}
          setStage={(v) => dispatch({ type: "SET_STAGE", v })}
          onRun={() => startRun()}
          onBack={() => dispatch({ type: "GO", screen: "hero" })}
          cta={CTA}
        />
      )}
      {s.screen === "loading" && (
        <LoadingGame url={s.url || "your site"} levels={GAME_LOADING} ready={s.loadingReady} onDone={onLoadingDone} />
      )}
      {s.screen === "report" && s.report && (
        <ReportGame data={s.report} onRerun={onRerun} onShare={onShare} onSave={onSave} toast={s.toast} />
      )}
      {s.screen === "error" && (
        <ErrorGame
          url={s.url}
          message={s.errorMessage}
          onRetry={() => dispatch({ type: "GO", screen: "analyze" })}
          onPaste={onPasteFallback}
        />
      )}
      {s.screen === "dashboard" && (
        <DashboardGame
          onPlay={onPlayFromDash}
          onHome={() => dispatch({ type: "GO", screen: "hero" })}
          onUpgrade={onUpgrade}
          authEmail={s.authEmail}
        />
      )}
      {s.screen === "login" && (
        <LoginGame onBack={() => dispatch({ type: "GO", screen: "hero" })} />
      )}
    </>
  );
}
